import './RevPARCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';


function RevPARCalculator() {
    const langPrefix = useLanguagePrefix();
    const [formulaType, setFormulaType] = useState(null);
    const [roomRevenue, setRoomRevenue] = useState(null);
    const [roomAvailable, setRoomAvailable] = useState(null);
    const [averageDailyRate, setAverageDailyRate] = useState(null);
    const [occupancyRate, setOccupancyRate] = useState(null);
    const [adr, setAdr] = useState(null);
    const { t } = useTranslation();

    const formulaTypeOptions = [
        { label: 'Revenue based', value: 'revenue' },
        { label: 'ADR based', value: 'adr' }
    ];

    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'revenue') {
            setRoomRevenue(value);
        } else if (type === 'available') {
            setRoomAvailable(value);
        } else if (type === 'averagedailyrate') {
            setAverageDailyRate(value);
        } else if (type === 'occupancy') {
            setOccupancyRate(value);
        }
    };


    const calculateRevenue = () => {
        if (roomRevenue > 0 && roomAvailable > 0) {
            const result = roomRevenue / roomAvailable;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setAdr(formatted);

        } else {
            setAdr(null);
        }
    }

    const calculateADR = () => {
        if (averageDailyRate > 0 && occupancyRate > 0) {
            const result = (averageDailyRate * occupancyRate);
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setAdr(formatted);
        } else {
            setAdr(null);
        }
    };

    return (
        <div>
            <Helmet>
                <title>RevPAR Calculator – Revenue Per Available Room Tool - Prostay</title>
                <meta name="description" content="Use Prostay’s free RevPAR calculator to measure your hotel’s revenue per available room. Learn how to calculate RevPAR, why it matters, and how to improve it." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/revpar-calculator/"} />

                <link rel="alternate" href="https://www.prostay.com/en/revpar-calculator/" />
                <link rel="alternate" href="https://www.prostay.com/es/revpar-calculator/" />

                <meta property="og:title" content="RevPAR Calculator – Revenue Per Available Room Tool - Prostay" />
                <meta property="og:description" content="Use Prostay’s free RevPAR calculator to measure your hotel’s revenue per available room. Learn how to calculate RevPAR, why it matters, and how to improve it." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/revpar-calculator/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="RevPAR Calculator – Revenue Per Available Room Tool - Prostay" />
                <meta name="twitter:description" content="Use Prostay’s free RevPAR calculator to measure your hotel’s revenue per available room. Learn how to calculate RevPAR, why it matters, and how to improve it." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />
            </Helmet>
            <div className='d-flex revpar-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('RevPAR Calculator - Revenue Per Available Room')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Calculate Your Hotel’s RevPAR in Seconds.')} <br />
                            {t('Use this free tool to instantly measure your property’s revenue performance based on both occupancy and room rates.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column'>
                                    <label>
                                        {t('Formula Type')}
                                    </label>
                                    <div className='select-container w-100' style={{ position: 'relative' }}>
                                        <select style={{ appearance: 'none', width: '100%' }} value={formulaType} onChange={(e) => setFormulaType(e.target.value)}>
                                            {
                                                formulaTypeOptions.map(type =>
                                                    <option key={type.value} value={type.value}>
                                                        {t(type.label)}
                                                    </option>
                                                )
                                            }
                                        </select>
                                        <svg width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', right: 20, top: 22 }}>
                                            <path d="M1 1.5L7 7.5L13 1.5" stroke="#808698" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                {formulaType === 'revenue' ?
                                    (
                                        <>
                                            <div className='d-flex flex-column mt-4'>
                                                <label>
                                                    {t('Total Room Revenue')}
                                                </label>
                                                <input
                                                    type='number'
                                                    value={roomRevenue}
                                                    onChange={(e) => handleInputChange(e, 'revenue')}
                                                    placeholder={t('Enter amount here')} />
                                            </div>
                                            <div className='d-flex flex-column mt-4'>
                                                <label>
                                                    {t('Total Room Available')}
                                                </label>
                                                <input
                                                    type='number'
                                                    value={roomAvailable}
                                                    onChange={(e) => handleInputChange(e, 'available')}
                                                    placeholder={t('Enter number here')} />
                                            </div>
                                        </>
                                    )
                                    : (<>
                                        <div className='d-flex flex-column mt-4'>
                                            <label>
                                                {t('Average Daily Rate')}
                                            </label>
                                            <input
                                                type='number'
                                                value={averageDailyRate}
                                                onChange={(e) => handleInputChange(e, 'averagedailyrate')}
                                                placeholder={t('Enter amount here')} />
                                        </div>
                                        <div className='d-flex flex-column mt-4'>
                                            <label>
                                                {t('Occupancy Rate')}
                                            </label>
                                            <input
                                                type='number'
                                                value={occupancyRate}
                                                onChange={(e) => handleInputChange(e, 'occupancy')}
                                                placeholder={t('Enter number here')} />
                                        </div>
                                    </>)}
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={formulaType === 'revenue' ? calculateRevenue : calculateADR}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('RevPAR')}
                                        </label>
                                        <input
                                            readOnly
                                            value={adr}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is RevPAR?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('RevPAR (Revenue Per Available Room) is one of the most important KPIs in hotel revenue management. It measures how efficiently a hotel is generating income from its room inventory — factoring in both how many rooms are sold and how much revenue they earn.')}
                                </p>
                                <br />
                                <p>
                                    {t('RevPAR helps hoteliers assess the combined impact of pricing strategy and occupancy, making it an essential metric for forecasting, budgeting, and performance benchmarking.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('RevPAR Formula')}
                        </h2>
                        <p>
                            {t('There are two common ways to calculate RevPAR:')}
                        </p>
                        <div className='d-flex align-items-center flex-column flex-sm-row formula gap-1'>
                            <div className='d-flex align-items-center gap-1'>
                                <p>
                                    {t('RevPAR')} =
                                </p>
                                <div className='d-flex flex-column align-items-center'>
                                    <p style={{ borderBottom: '0.5px solid #1643E0' }}>
                                        {t('Total Room Revenue')}
                                    </p>
                                    <p>
                                        {t('Available Rooms')}
                                    </p>
                                </div>
                            </div>
                            <p style={{ paddingInline: '25px' }}>
                                {t('or')}
                            </p>
                            <p>
                                {t('RevPAR = ADR x Occupancy Rate')}
                            </p>
                        </div>
                        <ul style={{ maxWidth: '500px' }}>
                            <li>
                                <p>
                                    {t('Total Room Revenue: Income from sold rooms (excluding taxes and extra services)')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('Available Rooms: Total sellable room inventory during the period')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('ADR: Average Daily Rate')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {t('Occupancy Rate: Percentage of rooms sold versus available')}
                                </p>
                            </li>
                        </ul>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('How to Calculate RevPAR')}
                        </h2>
                        <p>
                            {t('There are two primary ways to calculate RevPAR. The first and most direct method divides total room revenue by the number of available rooms in a given period. The second method multiplies your Average Daily Rate (ADR) by your occupancy rate, which achieves the same result.')}
                        </p>
                        <p>
                            {t('For example, if your hotel earns $25,000 from room sales and has 100 rooms available over the same period, your RevPAR would be $250.')}
                        </p>
                        <p>
                            {t('Alternatively, if your ADR is $200 and your occupancy rate is 85%, your RevPAR is also $170. Both formulas are valid and offer a clear picture of room revenue efficiency.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Why RevPAR Matters in Hospitality')}
                        </h2>
                        <p>
                            {t('RevPAR is one of the most valuable metrics in the hotel industry because it combines both pricing power and room occupancy into a single figure. While ADR focuses only on how much you charge per sold room and occupancy tells you how full your hotel is, RevPAR reflects the true revenue efficiency of your entire inventory. This means it accounts for both unsold and underperforming rooms.')}
                        </p>
                        <p>
                            {t('Hotels use RevPAR to identify high and low performing dates, compare departments or segments, and evaluate whether their current rate strategy is aligned with market demand.')}
                        </p>
                        <p>
                            {t('By tracking RevPAR regularly, hotel teams can make smarter decisions about rate adjustments, discount policies, and demand management, ultimately improving bottom-line performance.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Understanding the Difference Between RevPAR and ADR')}
                        </h2>
                        <p>
                            {t('Although RevPAR and ADR are closely related, they serve different purposes in hotel revenue management. ADR, or Average Daily Rate, measures how much you’re earning for rooms that are sold, but it doesn’t account for empty rooms.')}
                        </p>
                        <p>
                            {t('RevPAR, on the other hand, includes unsold rooms in its calculation, which gives a more comprehensive view of how your inventory is performing overall. This makes RevPAR a more useful metric when analyzing the efficiency of your rate strategy in combination with actual booking performance.')}
                        </p>
                        <p>
                            {t('A hotel with a high ADR but low RevPAR may be overpricing and losing occupancy, while one with strong RevPAR and moderate ADR is likely striking the right balance between rate and demand.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Related Tools for Hotel Performance')}
                        </h2>
                        <p>
                            {t('If you’re using RevPAR to understand your property’s revenue performance, consider exploring other financial tools designed for hoteliers. The')} <a href={`${langPrefix}/adr-calculator/`}>{t('ADR Calculator')}</a> {t('helps you isolate your pricing power, while the ')} <a href={`${langPrefix}/occupancy-rate/`}>{t('Occupancy Rate Calculator')}</a> {t('tracks how many rooms are being filled.')}
                        </p>
                        <p>
                            {t('The ')} <a href={`${langPrefix}/goppar-calculator/`}>{t('GOPPAR ')}</a> {t('tool helps you evaluate profit per available room by including operational costs, and the ')} <a href={`${langPrefix}/trevpar-calculator/`}>{t('TRevPAR calculator')}</a> {t('expands your view beyond rooms to include total revenue from food, spa, and other departments.')}
                        </p>
                        <p>
                            {t('These tools work together to provide a complete picture of how your hotel is performing financially and operationally.')}
                        </p>
                        <p>
                            {t('Prostay goes beyond simple metrics by integrating RevPAR tracking, rate management, and reporting directly into your daily workflow. Our all-in-one ')} <a onClick={() => {
                                const link = 'https://app.prostay.com';
                                const a = document.createElement('a');
                                a.href = link;
                                a.target = '_blank';
                                a.rel = 'noopener noreferrer'; // Securitate suplimentară
                                a.click();
                            }}>{t('hotel management platform')}</a> {t('allows you to automate pricing decisions, monitor live data, and make adjustments in real time.')}
                        </p>
                        <p>
                            {t('With RevPAR and other key performance indicators built into the core system, your hotel gains a deeper level of financial insight and operational control. From boutique resorts to multi-property groups, Prostay helps you drive smarter revenue strategies without spreadsheets or manual tracking.')}
                        </p>
                    </div>
                </div>
                <ToolFixedMenu />
            </div>
        </div>
    )
}

export default RevPARCalculator;